import get from 'lodash/get';
import { format } from 'date-fns';
import router from '@/router';
import { queryArgs, siteId, siteIds, dates, objects } from '@/utils';

let shouldCache = true;
export default {
	fetchLeads({ commit, dispatch, getters, rootGetters }) {
		return new Promise((resolve, reject) => {
			if (!rootGetters.connected) {
				return resolve();
			}

			commit('loading', 'monsterleads', { root: true });
			if (!rootGetters.isFetched('monsterleads')) {
				commit('fetching', 'monsterleads', { root: true });
			}

			return this._vm.$api
				.get({ endpoint: 'leads' + getters.leadsQueryString, cached: shouldCache })
				.then(({ body }) => {
					dispatch('fetchWpCampaignStatusSetting', body.campaigns).then((campaigns) => {
						commit('setLeadsPageData', { ...body, campaigns });
						commit('fetched', 'monsterleads', { root: true });
						commit('doneLoading', 'monsterleads', { root: true });
						resolve();
					});
				})
				.catch(() => {
					dispatch('handleError', {
						explanation: 'Something went wrong while fetching contacts.',
						reject,
					});
				})
				.finally(() => {
					shouldCache = true;
				});
		});
	},

	fetchLeadsCampaigns({ commit, dispatch, getters, rootGetters }) {
		return new Promise((resolve, reject) => {
			if (!rootGetters.connected) {
				return resolve();
			}

			commit('loading', 'lead-campaigns', { root: true });
			if (!rootGetters.isFetched('lead-campaigns')) {
				commit('fetching', 'lead-campaigns', { root: true });
			}

			return this._vm.$api
				.get({ endpoint: 'leads/campaigns' + getters.leadsQueryString, cached: false })
				.then(({ body }) => {
					dispatch('fetchWpCampaignStatusSetting', body.campaigns).then((campaigns) => {
						commit('setLeadCampaignPageData', (body = { ...body, campaigns }));
						commit('fetched', 'lead-campaigns', { root: true });
						commit('doneLoading', 'lead-campaigns', { root: true });

						resolve();
					});
				})
				.catch(({ ...error }) => {
					dispatch('handleError', {
						explanation: 'Something went wrong while fetching campaigns with subscribers.',
						error,
						reject,
					});
				});
		});
	},

	fetchLeadsTags({ commit, dispatch, getters, rootGetters }) {
		return new Promise((resolve, reject) => {
			if (!rootGetters.connected) {
				return resolve();
			}

			commit('loading', 'lead-tags', { root: true });
			if (!rootGetters.isFetched('lead-tags')) {
				commit('fetching', 'lead-tags', { root: true });
			}

			const query = getters.leadsQueryString.replace('searchLead', 'search');

			return this._vm.$api
				.get({ endpoint: 'leads/tags' + query, cached: false })
				.then(({ body }) => {
					commit('fetched', 'lead-tags', { root: true });
					commit('doneLoading', 'lead-tags', { root: true });
					commit('setLeadTagPageData', body);
					resolve();
				})
				.catch(({ ...error }) => {
					dispatch('handleError', {
						explanation: 'Something went wrong while fetching segments.',
						error,
						reject,
					});
				});
		});
	},

	createTag({ commit, dispatch, getters, rootGetters }, tagName) {
		return new Promise((resolve, reject) => {
			if (!rootGetters.connected) {
				return resolve();
			}

			return this._vm.$api
				.put('leads/tags/' + encodeURIComponent(tagName || ' ') + getters.leadsQueryString)
				.then(() => {
					shouldCache = false;
					dispatch('fetchLeadsTags');
					resolve();
				})
				.catch(({ ...error }) => {
					if (303 === error.status) {
						commit(
							'addAlert',
							{
								type: 'warn',
								closes: true,
								message: 'This tag already exists.',
							},
							{ root: true }
						);

						return reject(error);
					}

					dispatch('handleError', {
						explanation: 'Something went wrong while creating this tag.',
						error,
						reject,
					});
				});
		});
	},

	fetchLeadsFromDateRange({ state, commit, dispatch }, payload) {
		if (!objects.areEqual(payload, state.dateRange)) {
			commit('updateDateRange', payload);
			dispatch('fetchLeads');
		}
	},

	fetchStatsFromDateRange({ state, commit, dispatch }, payload) {
		if (!objects.areEqual(payload, state.dateRange)) {
			commit('updateDateRange', payload);
			dispatch('fetchStats');
		}
	},

	fetchStats({ commit, dispatch, getters, rootGetters }) {
		return new Promise((resolve, reject) => {
			if (!rootGetters.connected) {
				return resolve();
			}

			commit('loading', 'lead-stats', { root: true });
			if (!rootGetters.isFetched('lead-stats')) {
				commit('fetching', 'lead-stats', { root: true });
			}

			let queryString = '';

			if (getters.startEnd.start) {
				queryString = queryArgs.add('dateStart', dates.timestamp(getters.startEnd.start), queryString);
			}

			if (getters.startEnd.end) {
				queryString = queryArgs.add('dateEnd', dates.timestamp(getters.startEnd.end), queryString);
			}

			queryString = queryArgs.add('siteIds', siteIds ? siteIds.join(',') : siteId, queryString);

			return this._vm.$api
				.get('leads/stats?' + queryString)
				.then(({ body }) => {
					commit('fetched', 'lead-stats', { root: true });
					commit('doneLoading', 'lead-stats', { root: true });
					commit('setLeadStats', body);
					resolve();
				})
				.catch(({ ...error }) => {
					dispatch('handleError', {
						explanation: 'Something went wrong while fetching subscriber stats.',
						error,
						reject,
					});
				});
		});
	},

	fetchTotalCount({ commit, dispatch }) {
		return new Promise((resolve, reject) => {
			this._vm.$api
				.get('leads/count')
				.then(({ body }) => {
					commit('setExportCount', body || 0);
					resolve();
				})
				.catch(({ ...error }) => {
					dispatch('handleError', {
						explanation: 'Something went wrong while fetching export count.',
						error,
						reject,
					});
				});
		});
	},

	export({ state, dispatch }, args = {}) {
		return new Promise((resolve, reject) => {
			const queryString = 0 < Object.keys(args).length ? '?' + queryArgs.serialize(args) : '';

			this._vm.$api
				.get({ endpoint: `leads/export${queryString}`, cached: false })
				.then(({ text, statusCode }) => {
					if ('204' === String(statusCode) || !text.length) {
						return resolve(statusCode);
					}

					const url = window.URL.createObjectURL(new Blob([text]));
					const link = document.createElement('a');

					if ('undefined' === typeof link.download) {
						window.location = url;
					} else {
						link.href = url;
						link.style.display = 'none';

						let fileName = 'MonsterLeads-';
						fileName += format(new Date(), 'yyyy-MM-dd');
						fileName += '.' + get(args, 'export.type', get(state, 'export.type', 'csv'));
						link.setAttribute('download', fileName);

						document.body.appendChild(link);
						link.click();
					}

					// Revoke the object URL after some time to free up resources
					// There is no way to find out whether user finished downloading
					setTimeout(function () {
						window.URL.revokeObjectURL(url);
						link.remove();
					}, 60 * 1000);

					return resolve(statusCode);
				})
				.catch(({ ...error }) => {
					dispatch('handleError', {
						explanation: 'Something went wrong while exporting data.',
						error,
						reject,
					});
				});
		});
	},

	handleError({ commit }, { explanation, error, reject }) {
		if (explanation) {
			error.explanation = explanation;
		}

		error.closes = true;
		commit('maybeAddError', error, { root: true });
		// eslint-disable-next-line no-console
		console.error({ error });
		reject(error);
	},

	deleteLead({ commit, dispatch }, leadId) {
		return new Promise((resolve, reject) => {
			this._vm.$api
				.delete(`leads/${leadId}`)
				.then(() => {
					commit('removeLead', leadId);
					resolve();
				})
				.catch(({ ...error }) => {
					dispatch('handleError', {
						explanation: `Something went wrong while deleting contact (${leadId}).`,
						error,
						reject,
					});
				});
		});
	},

	updateSiteFilters({ commit, dispatch }, siteData) {
		commit('updateSiteFilters', siteData);
		dispatch('fetchLeads');
	},

	updateCampaignFilter({ commit, dispatch }, campaignSlug) {
		commit('updateCampaignFilter', campaignSlug);
		dispatch('fetchLeads');
	},

	updateTagFilter({ commit, dispatch }, tagId) {
		commit('updateTagFilter', tagId);
		dispatch('fetchLeads');
	},

	updateSearch({ commit, dispatch }, search) {
		commit('setSearch', search);
		dispatch('fetchLeads');
	},

	updateTagSearch({ commit, dispatch }, search) {
		commit('setSearch', search);
		dispatch('fetchLeadsTags');
	},

	changePage({ commit, dispatch }, page) {
		commit('page', page);
		dispatch('fetchLeads');
	},

	resetPageVar({ commit }) {
		commit('page', 1);
	},

	setPageVars({ commit }, { tab = null, page = null, campaign = null, tag = null }) {
		if (null !== campaign) {
			tab = 'contacts';
			commit('updateCampaignFilter', campaign);
		}

		if (null !== tag) {
			tab = 'contacts';
			commit('updateTagFilter', tag);
		}

		if (null !== tab) {
			commit('tabs/setTab', { page: 'monsterleads', tab }, { root: true });
		}

		if (null !== page) {
			commit('page', page);
		}
	},

	goToPage({ state, getters, dispatch, rootGetters }, { tab = null, page = null, campaign = null, tag = null }) {
		const params = {};
		let name = 'monsterleads';

		dispatch('setPageVars', { tab, page, campaign, tag });

		if (rootGetters['tabs/monsterleadsTab']) {
			params.selectedTab = rootGetters['tabs/monsterleadsTab'];
			name = 'monsterleads-tab';
		}

		if (state.page) {
			params.leadsPage = state.page;
			if (params.selectedTab) {
				name = 'monsterleads-paginated-tab';
			}
		}

		if (getters.campaignFilter) {
			params.campaignId = getters.campaignFilter;
			name = params.leadsPage ? 'monsterleads-paginated-campaign' : 'monsterleads-campaign';
		} else if (getters.tagFilter) {
			params.tagId = getters.tagFilter;
			name = params.leadsPage ? 'monsterleads-paginated-tag' : 'monsterleads-tag';
		}

		router.push({ name, params });
	},

	goToTagFilteredPage({ dispatch }, tag) {
		dispatch('goToPage', {
			tab: 'contacts',
			page: 1,
			campaign: '',
			tag,
		});
	},

	goToCampaignFilteredPage({ dispatch }, campaign) {
		dispatch('goToPage', {
			tab: 'contacts',
			page: 1,
			tag: '',
			campaign,
		});
	},

	fetchWpCampaignStatusSetting({ dispatch }, campaigns) {
		const wpCampaigns = campaigns.map((campaign) => {
			return dispatch('campaigns/fetchWpCampaign', campaign.slug, { root: true })
				.then((wpCampaign) => {
					campaign.enabled = !!parseInt(wpCampaign.post_meta._omapi_enabled);
					return campaign;
				})
				.catch(() => {
					campaign.enabled = false;
					return campaign;
				});
		});

		return Promise.all(wpCampaigns);
	},
};
